// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Book, Image, AlertCircle } from 'lucide-react';
import { buildApiUrl } from '../config/api';
import { getAuthHeaders } from '../utils/apiAuth';
import './BookCover.css';

const BookCover = ({ 
  book, 
  size = 'medium', 
  showStatus = true, 
  className = '',
  onClick = null 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [coverUrl, setCoverUrl] = useState(null);

  // Tailles prédéfinies
  const sizeClasses = {
    small: 'book-cover-small',
    medium: 'book-cover-medium',
    large: 'book-cover-large',
    xlarge: 'book-cover-xlarge'
  };

  useEffect(() => {
    // Déterminer l'URL de la couverture.
    // On privilégie une éventuelle URL externe (Google Books/OpenLibrary), sinon
    // on tente TOUJOURS l'endpoint autoritaire /api/books/{id}/cover qui sert la
    // jaquette générée OU la couverture extraite (et renvoie 404 -> placeholder).
    // Cela rend l'affichage indépendant de l'état `has_cover` en base.
    let url = null;
    let estInterne = false;

    if (book.metadata?.cover_url) {
      url = book.metadata.cover_url;
    } else if (book.book_id) {
      url = buildApiUrl(`books/${book.book_id}/cover`);
      estInterne = true;
    } else if (book.cover_path) {
      url = buildApiUrl(`cover-extraction/image/${encodeURIComponent(book.cover_path)}`);
      estInterne = true;
    }

    setImageLoaded(false);
    setImageError(false);

    // URL externe : rien à authentifier, le navigateur la charge directement.
    if (!url || !estInterne) {
      setCoverUrl(url);
      return undefined;
    }

    // Endpoint interne : quand l'authentification est active, il exige un jeton.
    // Or une balise <img> ne peut PAS porter d'en-tête `Authorization` — c'est
    // pourquoi toutes les vignettes tombaient en 401 et restaient vides.
    //
    // On récupère donc l'image par `fetch`, avec les mêmes en-têtes que le reste
    // de l'application, puis on l'affiche via une URL d'objet locale.
    //
    // On ne passe DÉLIBÉRÉMENT pas le jeton en paramètre d'URL comme pour le SSE :
    // une couverture est mise en cache par le navigateur, tracée dans les journaux
    // du serveur et transmise en `Referer`. Le jeton y fuiterait à chaque vignette.
    let annule = false;
    let urlObjet = null;

    fetch(url, { headers: getAuthHeaders() })
      .then((reponse) => {
        if (!reponse.ok) throw new Error(String(reponse.status));
        return reponse.blob();
      })
      .then((blob) => {
        if (annule) return;
        urlObjet = URL.createObjectURL(blob);
        setCoverUrl(urlObjet);
      })
      .catch(() => {
        // 404 (pas de couverture) comme 401 : on retombe sur le placeholder,
        // exactement comme le faisait `onError` auparavant.
        if (!annule) {
          setCoverUrl(null);
          setImageError(true);
        }
      });

    return () => {
      annule = true;
      // Sans révocation, chaque rendu laisserait un blob en mémoire : sur une
      // bibliothèque de plusieurs centaines de livres, la fuite est réelle.
      if (urlObjet) URL.revokeObjectURL(urlObjet);
    };
  }, [book]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const handleClick = () => {
    if (onClick) {
      onClick(book);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'indexed':
        return <div className="status-icon indexed" title="Indexé" />;
      case 'received':
      case 'processing':
        return <div className="status-icon processing" title="En cours" />;
      case 'error':
        return <div className="status-icon error" title="Erreur" />;
      default:
        return <div className="status-icon pending" title="En attente" />;
    }
  };

  const renderCover = () => {
    if (coverUrl && !imageError) {
      return (
        <img
          src={coverUrl}
          alt={`Couverture de ${book.title || book.file_original_name}`}
          className={`book-cover-image ${imageLoaded ? 'loaded' : 'loading'}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading="lazy"
        />
      );
    }

    // Fallback : placeholder avec icône
    return (
      <div className="book-cover-placeholder">
        <Book size={size === 'small' ? 16 : size === 'large' ? 48 : 32} />
        {imageError && (
          <div className="cover-error-indicator">
            <AlertCircle size={16} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className={`book-cover ${sizeClasses[size]} ${className} ${onClick ? 'clickable' : ''}`}
      onClick={handleClick}
    >
      {renderCover()}
      
      {showStatus && book.status && (
        <div className="book-cover-status">
          {getStatusIcon(book.status)}
        </div>
      )}
      
      {/* Indicateur de chargement */}
      {coverUrl && !imageLoaded && !imageError && (
        <div className="book-cover-loading">
          <div className="loading-spinner" />
        </div>
      )}
      
      {/* Badge de qualité si disponible */}
      {book.metadata?.cover_quality && (
        <div className="book-cover-quality">
          {book.metadata.cover_quality === 'high' && (
            <span className="quality-badge high" title="Haute qualité">HD</span>
          )}
          {book.metadata.cover_quality === 'medium' && (
            <span className="quality-badge medium" title="Qualité moyenne">MQ</span>
          )}
        </div>
      )}
    </div>
  );
};

export default BookCover;
